import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    forwardRef,
    HostListener,
    Input,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import {ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR} from '@angular/forms';
import {BehaviorSubject} from 'rxjs';
import {Unsubscribable} from '@core/interfaces/unsubscribable';
import {ActivatedRoute} from '@angular/router';
import {debounceTime, distinctUntilChanged, takeUntil} from 'rxjs/operators';
import {NbAutocompleteDirective} from '@nebular/theme';

interface DefaultInterface {
    name: string;
    id: number;
    description: string;
}

@Component({
    selector: 'ngx-custom-autocomplete',
    templateUrl: './custom-autocomplete.component.html',
    styleUrls: ['./custom-autocomplete.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CustomAutocompleteComponent),
            multi: true,
        },
    ],
})
export class CustomAutocompleteComponent<T = DefaultInterface>
    extends Unsubscribable
    implements OnInit, AfterViewInit, ControlValueAccessor
{
    @ViewChild('inputRef', {read: NbAutocompleteDirective, static: false})
    autocompleteDirective: NbAutocompleteDirective<T>;

    @Output() routeChange: EventEmitter<number> = new EventEmitter();
    @Output() selectedChange: EventEmitter<T> = new EventEmitter();
    @Output() onSearch: EventEmitter<T | string> = new EventEmitter();
    @Output() onBlur: EventEmitter<boolean> = new EventEmitter();
    @Input() options: FormControl = new FormControl([]);
    @Input() id?: string;
    @Input() noOptionsLabelText?: string; // Optional custom static string
    @Input() useCreateLabel?: boolean; // Flag to turn on the reusable label function
    @Input() infoOptionDisabled: boolean = true;
    @Input() customTemplate?: any;
    @Input() set selectedName(value: T) {
        if (!this.inputFormControl.value) {
            this.isValueSelected = true;
            this.inputFormControl.setValue(value, {emitEvent: false});
        }
    }
    @Input() placeholder: string = '';

    @Input() viewHandle = (value: T | DefaultInterface): string => {
        return ((typeof value === 'object' && 'name' in value && value?.name) || value) as string;
    };

    optionsList = new BehaviorSubject(null);
    options$ = this.optionsList.asObservable();

    inputFormControl: FormControl = new FormControl('');

    touched = false;
    disabled = false;

    // Prevents triggering search/autocomplete logic when:
    // 1. A value is set programmatically (e.g., via @Input or writeValue)
    // 2. A value is selected from the dropdown
    public isValueSelected = false;

    private defaultCreateLabelFn = (searchStr: string = '') => `Create new value: "${searchStr}"`;

    onChange = (value) => {};

    onTouched = () => {};

    // Wait until Angular finishes rendering to avoid early interaction
    autocompleteReady = false;

    @HostListener('paste', ['$event'])
    onPaste() {
        this.isValueSelected = false;
    }

    @HostListener('cut', ['$event'])
    onCut() {
        this.isValueSelected = false;
    }

    constructor(private route: ActivatedRoute) {
        super();
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            this.autocompleteReady = true;
        });
    }

    ngOnInit(): void {
        let _id = +this.route?.snapshot?.params?.id;
        this.routeChange.emit(!isNaN(_id) ? +_id : null);
        this.inputFormControl.valueChanges
            .pipe(takeUntil(this.unsubscribe$), debounceTime(500), distinctUntilChanged())
            .subscribe((value) => {
                if (this.isValueSelected) {
                    this.isValueSelected = false;
                } else this.onSearch.emit(value);
            });

        this.options.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((options) => {
            this.optionsList.next(options);

            this.handleAutocompleteOpening();
        });
    }

    getNoOptionsLabel(search: string): string {
        if (this.noOptionsLabelText) {
            return this.noOptionsLabelText;
        }

        if (this.useCreateLabel) {
            return this.defaultCreateLabelFn(search);
        }

        return 'no data found';
    }

    public shouldDisplayOptionalLabel(search: string): boolean {
        if (this.optionsList.value.includes(search)) return false;
        // infoOptionDisabled handles whether we can create a new value with this autocomplete
        // if we can create a new value, we only want to when there is a value in the input
        if (!this.infoOptionDisabled) {
            return !!this.inputFormControl.value;
        } else {
            return !this.optionsList.value.length;
        }
    }

    onSelectedChange(value: T) {
        this.selectedChange.emit(value);
        this.onChange(value);

        this.isValueSelected = true;
    }

    writeValue(value: T): void {
        if (value) {
            this.isValueSelected = true;
            this.inputFormControl.setValue(value);
        }
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    openAutocomplete() {
        if (this.autocompleteDirective && !this.autocompleteDirective.isOpen) {
            this.autocompleteDirective.show();
        }
    }

    private handleAutocompleteOpening(): void {
        if (this.isValueSelected) {
            this.isValueSelected = false;
            this.closeAutocomplete(); // Close dropdown if value was set programmatically
        } else {
            this.openAutocomplete(); // Open dropdown if user typed
        }
    }

    closeAutocomplete() {
        if (this.autocompleteDirective && this.autocompleteDirective.isOpen) {
            this.autocompleteDirective.hide();
        }
    }
}
